import { CopyButton } from "@components/shared/CopyButton";
import { GENERATE_LINKEDIN_JOB_POSTING, UPDATE_CAMPAIGN_JOB_POSTING } from "@lib/graphql/mutations";
import { CAMPAIGN_LINKEDIN_JOB_POSTING_QUERY } from "@lib/graphql/queries";
import { useCampaignChannel } from "@lib/hooks/useCampaignChannel";
import { AlertTriangle, ArrowLeft, Info, Linkedin, Loader2, Sparkles } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { useMutation, useQuery } from "urql";

interface LinkedinJobPostingVersion {
  id: string;
  body: string;
  generationState: "draft" | "generating" | "error";
  generationError: string | null;
  model: string | null;
  versionNumber: number;
  effectiveFrom: string;
  changeSummary: string | null;
}

interface LinkedinJobPosting {
  id: string;
  kind: string;
  totalVersions: number;
  currentVersion: LinkedinJobPostingVersion | null;
}

interface CampaignData {
  id: string;
  title: string;
  linkedinJobPosting: LinkedinJobPosting | null;
}

const LINKEDIN_SOFT_LIMIT = 2000;

export function LinkedinJobPostingPage() {
  const { id } = useParams();
  const [{ data, fetching }, reexecute] = useQuery<{ campaign: CampaignData }>({
    query: CAMPAIGN_LINKEDIN_JOB_POSTING_QUERY,
    variables: { id },
  });
  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useCampaignChannel(id!, refresh);

  const [, generateMutation] = useMutation(GENERATE_LINKEDIN_JOB_POSTING);
  const [{ fetching: saving }, updateMutation] = useMutation(UPDATE_CAMPAIGN_JOB_POSTING);

  const [body, setBody] = useState("");
  const [dirty, setDirty] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const lastVersionIdRef = useRef<string | null>(null);

  const campaign = data?.campaign;
  const posting = campaign?.linkedinJobPosting ?? null;
  const currentVersion = posting?.currentVersion ?? null;
  const generationState = currentVersion?.generationState ?? null;

  useEffect(() => {
    if (!currentVersion) return;
    if (currentVersion.id === lastVersionIdRef.current) return;
    lastVersionIdRef.current = currentVersion.id;
    setBody(currentVersion.body ?? "");
    setDirty(false);
  }, [currentVersion]);

  const handleGenerate = async () => {
    setErrorMessage(null);
    const result = await generateMutation({ input: { campaignId: id } });
    const errors = result.data?.generateLinkedinJobPosting?.errors ?? [];
    if (errors.length) setErrorMessage(errors.join("; "));
    refresh();
  };

  const handleSave = async () => {
    if (!posting) return;
    setErrorMessage(null);
    const result = await updateMutation({
      input: { id: posting.id, body },
    });
    const errors = result.data?.updateCampaignJobPosting?.errors ?? [];
    if (errors.length) {
      setErrorMessage(errors.join("; "));
      return;
    }
    setDirty(false);
  };

  if (fetching && !campaign) {
    return <div className="p-6 text-sm text-text-muted">Loading LinkedIn job posting…</div>;
  }

  if (!campaign) {
    return <div className="p-6 text-sm text-text-muted">Campaign not found.</div>;
  }

  return (
    <div className="mx-auto max-w-3xl p-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <Link
          to={`/campaigns/${campaign.id}/overview`}
          className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary"
        >
          <ArrowLeft size={14} />
          Back to campaign
        </Link>
        {currentVersion && (
          <span className="text-xs text-text-faint" data-testid="version-count">
            Version {currentVersion.versionNumber} · {posting?.totalVersions ?? 0} total
          </span>
        )}
      </div>

      <div className="mb-4 flex items-start gap-3">
        <Linkedin size={22} className="mt-1 text-[#0a66c2]" />
        <div>
          <h1 className="text-xl font-semibold text-text-primary">LinkedIn Job Posting</h1>
          <p className="text-sm text-text-muted">
            Drafted from the brief, role blueprint, and your organisation's AI guidance.
          </p>
        </div>
      </div>

      {errorMessage && (
        <div
          className="mb-4 flex items-start gap-2 rounded border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
          data-testid="error-message"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      {!currentVersion && <EmptyState campaignTitle={campaign.title} onGenerate={handleGenerate} />}

      {generationState === "generating" && <GeneratingState />}

      {(generationState === "draft" || generationState === "error") && currentVersion && (
        <DraftEditor
          body={body}
          dirty={dirty}
          saving={saving}
          generationError={generationState === "error" ? currentVersion.generationError : null}
          onBodyChange={(value) => {
            setBody(value);
            setDirty(value !== (currentVersion.body ?? ""));
          }}
          onSave={handleSave}
          onRegenerate={handleGenerate}
        />
      )}

      <LinkedinFilterHint />
    </div>
  );
}

function EmptyState({
  campaignTitle,
  onGenerate,
}: {
  campaignTitle: string;
  onGenerate: () => void;
}) {
  const [triggering, setTriggering] = useState(false);
  return (
    <div
      className="rounded-lg border border-dashed border-border bg-card p-6 text-center"
      data-testid="empty-state"
    >
      <Sparkles size={24} className="mx-auto text-text-faint" />
      <h2 className="mt-3 text-sm font-medium text-text-primary">No LinkedIn posting yet</h2>
      <p className="mx-auto mt-1 max-w-sm text-sm text-text-muted">
        Generate a LinkedIn-formatted draft for <strong>{campaignTitle}</strong> using the brief and
        role blueprint. You can edit it after.
      </p>
      <button
        type="button"
        onClick={async () => {
          setTriggering(true);
          try {
            await onGenerate();
          } finally {
            setTriggering(false);
          }
        }}
        disabled={triggering}
        className="mt-4 inline-flex items-center gap-2 rounded bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent/90 disabled:opacity-50"
      >
        {triggering ? (
          <>
            <Loader2 size={14} className="animate-spin" />
            Starting…
          </>
        ) : (
          <>
            <Sparkles size={14} />
            Generate from brief and blueprint
          </>
        )}
      </button>
    </div>
  );
}

function GeneratingState() {
  return (
    <div
      className="flex items-start gap-3 rounded-lg border border-border bg-card p-6"
      data-testid="generating-state"
    >
      <Loader2 size={18} className="mt-0.5 shrink-0 animate-spin text-accent" />
      <div>
        <p className="text-sm font-medium text-text-primary">Drafting your LinkedIn posting…</p>
        <p className="mt-1 text-sm text-text-muted">
          This usually takes under a minute. We'll refresh automatically when it's ready.
        </p>
      </div>
    </div>
  );
}

interface DraftEditorProps {
  body: string;
  dirty: boolean;
  saving: boolean;
  generationError: string | null;
  onBodyChange: (value: string) => void;
  onSave: () => void;
  onRegenerate: () => void;
}

function DraftEditor({
  body,
  dirty,
  saving,
  generationError,
  onBodyChange,
  onSave,
  onRegenerate,
}: DraftEditorProps) {
  const overLimit = body.length > LINKEDIN_SOFT_LIMIT;
  return (
    <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
      {generationError && (
        <div
          className="mb-3 flex items-start gap-2 rounded border border-warning/40 bg-warning/10 p-3 text-sm text-text-primary"
          data-testid="generation-error"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-warning" />
          <div>
            <p className="font-medium">Last generation attempt failed</p>
            <p className="text-text-muted">{generationError}</p>
          </div>
        </div>
      )}
      <div className="mb-2 flex items-center justify-between">
        <span
          className={`text-xs ${overLimit ? "text-destructive" : "text-text-faint"}`}
          data-testid="char-count"
        >
          {body.length} / {LINKEDIN_SOFT_LIMIT} characters
        </span>
        <div className="flex items-center gap-2">
          <CopyButton
            ariaLabel="Copy LinkedIn posting"
            withLabel
            className="flex items-center gap-1 rounded border border-border px-2 py-1 text-xs font-medium text-text-secondary hover:bg-card-hover"
            getContent={() => ({ html: body, text: body })}
          />
          <button
            type="button"
            onClick={onRegenerate}
            className="flex items-center gap-1 rounded border border-border px-2 py-1 text-xs font-medium text-text-secondary hover:bg-card-hover"
          >
            <Sparkles size={14} />
            Regenerate
          </button>
        </div>
      </div>
      <textarea
        value={body}
        onChange={(event) => onBodyChange(event.target.value)}
        rows={22}
        className="w-full resize-y rounded border border-input-border bg-card-alt px-3 py-2 font-mono text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
        data-testid="body-editor"
      />
      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={onSave}
          disabled={!dirty || saving}
          className="rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent/90 disabled:opacity-50"
        >
          {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
      </div>
    </div>
  );
}

function LinkedinFilterHint() {
  return (
    <div
      className="mt-4 flex items-start gap-2 rounded border border-border bg-card-alt p-3 text-xs text-text-muted"
      data-testid="linkedin-filter-hint"
    >
      <Info size={14} className="mt-0.5 shrink-0 text-text-faint" />
      <p>
        When you publish on LinkedIn or a job board, configure applicant filters for right-to-work
        status and must-have criteria so the platform auto-screens at apply time.
      </p>
    </div>
  );
}
